/**
 * Cascading venue location options for the event wizard.
 * Country → State/region → City (cities are suggestions; free-text always allowed)
 */

export type VenueCountry = {
  name: string;
  states: VenueState[];
};

export type VenueState = {
  name: string;
  cities: string[];
};

function state(name: string, cities: string[] = []): VenueState {
  return { name, cities };
}

export const VENUE_COUNTRIES: VenueCountry[] = [
  {
    name: 'Australia',
    states: [
      state('Australian Capital Territory', ['Canberra']),
      state('New South Wales', ['Central Coast', 'Newcastle', 'Parramatta', 'Sydney', 'Wollongong']),
      state('Northern Territory', ['Alice Springs', 'Darwin', 'Palmerston']),
      state('Queensland', ['Brisbane', 'Cairns', 'Gold Coast', 'Sunshine Coast', 'Townsville']),
      state('South Australia', ['Adelaide', 'Mount Gambier', 'Whyalla']),
      state('Tasmania', ['Devonport', 'Hobart', 'Launceston']),
      state('Victoria', ['Ballarat', 'Bendigo', 'Geelong', 'Melbourne']),
      state('Western Australia', ['Bunbury', 'Fremantle', 'Mandurah', 'Perth']),
    ],
  },
  {
    name: 'Canada',
    states: [
      state('Alberta', ['Banff', 'Calgary', 'Edmonton', 'Red Deer']),
      state('British Columbia', ['Burnaby', 'Kelowna', 'Surrey', 'Vancouver', 'Victoria']),
      state('Manitoba', ['Brandon', 'Steinbach', 'Winnipeg']),
      state('New Brunswick', ['Fredericton', 'Moncton', 'Saint John']),
      state('Newfoundland and Labrador', ['Corner Brook', 'Gander', "St. John's"]),
      state('Northwest Territories', ['Yellowknife']),
      state('Nova Scotia', ['Dartmouth', 'Halifax', 'Sydney']),
      state('Nunavut', ['Iqaluit']),
      state('Ontario', ['Hamilton', 'Kitchener', 'London', 'Mississauga', 'Ottawa', 'Toronto']),
      state('Prince Edward Island', ['Charlottetown', 'Summerside']),
      state('Quebec', ['Gatineau', 'Laval', 'Montreal', 'Quebec City', 'Sherbrooke']),
      state('Saskatchewan', ['Moose Jaw', 'Regina', 'Saskatoon']),
      state('Yukon', ['Whitehorse']),
    ],
  },
  {
    name: 'France',
    states: [
      state('Auvergne-Rhône-Alpes', ['Clermont-Ferrand', 'Grenoble', 'Lyon', 'Saint-Étienne']),
      state('Bourgogne-Franche-Comté', ['Belfort', 'Besançon', 'Dijon']),
      state('Brittany', ['Brest', 'Quimper', 'Rennes', 'Vannes']),
      state('Centre-Val de Loire', ['Chartres', 'Orléans', 'Tours']),
      state('Corsica', ['Ajaccio', 'Bastia']),
      state('Grand Est', ['Metz', 'Nancy', 'Reims', 'Strasbourg']),
      state('Hauts-de-France', ['Amiens', 'Calais', 'Dunkirk', 'Lille']),
      state('Île-de-France', ['Boulogne-Billancourt', 'Paris', 'Saint-Denis', 'Versailles']),
      state('Normandy', ['Caen', 'Le Havre', 'Rouen']),
      state('Nouvelle-Aquitaine', ['Bordeaux', 'La Rochelle', 'Limoges', 'Poitiers']),
      state('Occitanie', ['Montpellier', 'Nîmes', 'Perpignan', 'Toulouse']),
      state('Pays de la Loire', ['Angers', 'Le Mans', 'Nantes', 'Saint-Nazaire']),
      state("Provence-Alpes-Côte d'Azur", ['Avignon', 'Cannes', 'Marseille', 'Nice', 'Toulon']),
    ],
  },
  {
    name: 'Germany',
    states: [
      state('Baden-Württemberg', ['Freiburg', 'Karlsruhe', 'Mannheim', 'Stuttgart']),
      state('Bavaria', ['Augsburg', 'Munich', 'Nuremberg', 'Regensburg']),
      state('Berlin', ['Berlin']),
      state('Brandenburg', ['Brandenburg an der Havel', 'Cottbus', 'Potsdam']),
      state('Bremen', ['Bremen', 'Bremerhaven']),
      state('Hamburg', ['Hamburg']),
      state('Hesse', ['Darmstadt', 'Frankfurt', 'Kassel', 'Wiesbaden']),
      state('Lower Saxony', ['Braunschweig', 'Hanover', 'Oldenburg', 'Osnabrück']),
      state('Mecklenburg-Vorpommern', ['Neubrandenburg', 'Rostock', 'Schwerin']),
      state('North Rhine-Westphalia', ['Bonn', 'Cologne', 'Dortmund', 'Düsseldorf', 'Essen']),
      state('Rhineland-Palatinate', ['Koblenz', 'Ludwigshafen', 'Mainz', 'Trier']),
      state('Saarland', ['Neunkirchen', 'Saarbrücken']),
      state('Saxony', ['Chemnitz', 'Dresden', 'Leipzig']),
      state('Saxony-Anhalt', ['Dessau', 'Halle', 'Magdeburg']),
      state('Schleswig-Holstein', ['Flensburg', 'Kiel', 'Lübeck']),
      state('Thuringia', ['Erfurt', 'Gera', 'Jena', 'Weimar']),
    ],
  },
  {
    name: 'India',
    states: [
      state('Andaman and Nicobar Islands', ['Port Blair']),
      state('Andhra Pradesh', ['Guntur', 'Kurnool', 'Nellore', 'Tirupati', 'Vijayawada', 'Visakhapatnam']),
      state('Arunachal Pradesh', ['Itanagar', 'Naharlagun', 'Tawang']),
      state('Assam', ['Dibrugarh', 'Guwahati', 'Jorhat', 'Silchar']),
      state('Bihar', ['Bhagalpur', 'Gaya', 'Muzaffarpur', 'Patna']),
      state('Chandigarh', ['Chandigarh']),
      state('Chhattisgarh', ['Bhilai', 'Bilaspur', 'Durg', 'Raipur']),
      state('Dadra and Nagar Haveli and Daman and Diu', ['Daman', 'Diu', 'Silvassa']),
      state('Delhi', ['Delhi', 'New Delhi']),
      state('Goa', ['Mapusa', 'Margao', 'Panaji', 'Vasco da Gama']),
      state('Gujarat', ['Ahmedabad', 'Bhavnagar', 'Gandhinagar', 'Rajkot', 'Surat', 'Vadodara']),
      state('Haryana', ['Ambala', 'Faridabad', 'Gurugram', 'Hisar', 'Karnal', 'Panipat']),
      state('Himachal Pradesh', ['Dharamshala', 'Manali', 'Shimla', 'Solan']),
      state('Jammu and Kashmir', ['Anantnag', 'Jammu', 'Srinagar']),
      state('Jharkhand', ['Bokaro', 'Dhanbad', 'Jamshedpur', 'Ranchi']),
      state('Karnataka', ['Bangalore', 'Belgaum', 'Hubli', 'Mangalore', 'Mangaluru', 'Mysore']),
      state('Kerala', ['Kannur', 'Kochi', 'Kozhikode', 'Thiruvananthapuram', 'Thrissur']),
      state('Ladakh', ['Kargil', 'Leh']),
      state('Lakshadweep', ['Kavaratti']),
      state('Madhya Pradesh', ['Bhopal', 'Gwalior', 'Indore', 'Jabalpur', 'Ujjain']),
      state('Maharashtra', ['Aurangabad', 'Mumbai', 'Nagpur', 'Nashik', 'Navi Mumbai', 'Pune', 'Thane']),
      state('Manipur', ['Imphal']),
      state('Meghalaya', ['Shillong']),
      state('Mizoram', ['Aizawl']),
      state('Nagaland', ['Dimapur', 'Kohima']),
      state('Odisha', ['Bhubaneswar', 'Cuttack', 'Puri', 'Rourkela']),
      state('Puducherry', ['Karaikal', 'Mahe', 'Puducherry', 'Yanam']),
      state('Punjab', ['Amritsar', 'Chandigarh', 'Jalandhar', 'Ludhiana', 'Patiala']),
      state('Rajasthan', ['Ajmer', 'Bikaner', 'Jaipur', 'Jodhpur', 'Kota', 'Udaipur']),
      state('Sikkim', ['Gangtok']),
      state('Tamil Nadu', ['Chennai', 'Coimbatore', 'Madurai', 'Salem', 'Tiruchirappalli', 'Tirunelveli']),
      state('Telangana', ['Hyderabad', 'Karimnagar', 'Nizamabad', 'Secunderabad', 'Warangal']),
      state('Tripura', ['Agartala']),
      state('Uttar Pradesh', ['Agra', 'Ghaziabad', 'Kanpur', 'Lucknow', 'Noida', 'Prayagraj', 'Varanasi']),
      state('Uttarakhand', ['Dehradun', 'Haridwar', 'Nainital', 'Rishikesh']),
      state('West Bengal', ['Asansol', 'Durgapur', 'Howrah', 'Kolkata', 'Siliguri']),
    ],
  },
  {
    name: 'Japan',
    states: [
      state('Aichi', ['Nagoya', 'Toyota']),
      state('Akita', ['Akita']),
      state('Aomori', ['Aomori', 'Hachinohe']),
      state('Chiba', ['Chiba', 'Funabashi', 'Narita']),
      state('Ehime', ['Matsuyama']),
      state('Fukui', ['Fukui']),
      state('Fukuoka', ['Fukuoka', 'Kitakyushu']),
      state('Fukushima', ['Fukushima', 'Koriyama']),
      state('Gifu', ['Gifu']),
      state('Gunma', ['Maebashi', 'Takasaki']),
      state('Hiroshima', ['Hiroshima']),
      state('Hokkaido', ['Asahikawa', 'Hakodate', 'Sapporo']),
      state('Hyogo', ['Himeji', 'Kobe']),
      state('Ibaraki', ['Mito', 'Tsukuba']),
      state('Ishikawa', ['Kanazawa']),
      state('Iwate', ['Morioka']),
      state('Kagawa', ['Takamatsu']),
      state('Kagoshima', ['Kagoshima']),
      state('Kanagawa', ['Kamakura', 'Kawasaki', 'Yokohama']),
      state('Kochi', ['Kochi']),
      state('Kumamoto', ['Kumamoto']),
      state('Kyoto', ['Kyoto']),
      state('Mie', ['Tsu', 'Yokkaichi']),
      state('Miyagi', ['Sendai']),
      state('Miyazaki', ['Miyazaki']),
      state('Nagano', ['Matsumoto', 'Nagano']),
      state('Nagasaki', ['Nagasaki']),
      state('Nara', ['Nara']),
      state('Niigata', ['Niigata']),
      state('Oita', ['Beppu', 'Oita']),
      state('Okayama', ['Okayama']),
      state('Okinawa', ['Naha', 'Okinawa']),
      state('Osaka', ['Osaka', 'Sakai']),
      state('Saga', ['Saga']),
      state('Saitama', ['Kawagoe', 'Saitama']),
      state('Shiga', ['Otsu']),
      state('Shimane', ['Matsue']),
      state('Shizuoka', ['Hamamatsu', 'Shizuoka']),
      state('Tochigi', ['Utsunomiya']),
      state('Tokushima', ['Tokushima']),
      state('Tokyo', ['Minato', 'Shibuya', 'Shinjuku', 'Tokyo']),
      state('Tottori', ['Tottori']),
      state('Toyama', ['Toyama']),
      state('Wakayama', ['Wakayama']),
      state('Yamagata', ['Yamagata']),
      state('Yamaguchi', ['Shimonoseki', 'Yamaguchi']),
      state('Yamanashi', ['Kofu']),
    ],
  },
  {
    name: 'Singapore',
    states: [
      state('Central Region', ['Downtown Core', 'Marina Bay', 'Orchard', 'Singapore']),
      state('East Region', ['Bedok', 'Changi', 'Pasir Ris', 'Tampines']),
      state('North Region', ['Sembawang', 'Woodlands', 'Yishun']),
      state('North-East Region', ['Hougang', 'Punggol', 'Sengkang', 'Serangoon']),
      state('West Region', ['Bukit Batok', 'Clementi', 'Jurong East', 'Jurong West']),
    ],
  },
  {
    name: 'United Arab Emirates',
    states: [
      state('Abu Dhabi', ['Abu Dhabi', 'Al Ain', 'Madinat Zayed']),
      state('Ajman', ['Ajman']),
      state('Dubai', ['Dubai']),
      state('Fujairah', ['Fujairah']),
      state('Ras Al Khaimah', ['Ras Al Khaimah']),
      state('Sharjah', ['Sharjah']),
      state('Umm Al Quwain', ['Umm Al Quwain']),
    ],
  },
  {
    name: 'United Kingdom',
    states: [
      state('England', [
        'Birmingham', 'Bristol', 'Cambridge', 'Leeds', 'Leicester', 'Liverpool',
        'London', 'Manchester', 'Newcastle', 'Nottingham', 'Oxford', 'Sheffield', 'Southampton',
      ]),
      state('Northern Ireland', ['Belfast', 'Derry', 'Lisburn', 'Newry']),
      state('Scotland', ['Aberdeen', 'Dundee', 'Edinburgh', 'Glasgow', 'Inverness', 'Stirling']),
      state('Wales', ['Bangor', 'Cardiff', 'Newport', 'Swansea', 'Wrexham']),
    ],
  },
  {
    name: 'United States',
    states: [
      state('Alabama', ['Birmingham', 'Huntsville', 'Mobile', 'Montgomery']),
      state('Alaska', ['Anchorage', 'Fairbanks', 'Juneau']),
      state('Arizona', ['Mesa', 'Phoenix', 'Scottsdale', 'Tucson']),
      state('Arkansas', ['Fayetteville', 'Fort Smith', 'Little Rock']),
      state('California', ['Los Angeles', 'Oakland', 'Sacramento', 'San Diego', 'San Francisco', 'San Jose']),
      state('Colorado', ['Aurora', 'Boulder', 'Colorado Springs', 'Denver']),
      state('Connecticut', ['Bridgeport', 'Hartford', 'New Haven', 'Stamford']),
      state('Delaware', ['Dover', 'Newark', 'Wilmington']),
      state('District of Columbia', ['Washington']),
      state('Florida', ['Jacksonville', 'Miami', 'Orlando', 'Tallahassee', 'Tampa']),
      state('Georgia', ['Athens', 'Atlanta', 'Augusta', 'Savannah']),
      state('Hawaii', ['Hilo', 'Honolulu', 'Kailua']),
      state('Idaho', ['Boise', "Coeur d'Alene", 'Idaho Falls']),
      state('Illinois', ['Aurora', 'Chicago', 'Naperville', 'Springfield']),
      state('Indiana', ['Bloomington', 'Fort Wayne', 'Indianapolis']),
      state('Iowa', ['Cedar Rapids', 'Des Moines', 'Iowa City']),
      state('Kansas', ['Kansas City', 'Lawrence', 'Topeka', 'Wichita']),
      state('Kentucky', ['Bowling Green', 'Lexington', 'Louisville']),
      state('Louisiana', ['Baton Rouge', 'Lafayette', 'New Orleans', 'Shreveport']),
      state('Maine', ['Augusta', 'Bangor', 'Portland']),
      state('Maryland', ['Annapolis', 'Baltimore', 'Rockville']),
      state('Massachusetts', ['Boston', 'Cambridge', 'Springfield', 'Worcester']),
      state('Michigan', ['Ann Arbor', 'Detroit', 'Grand Rapids', 'Lansing']),
      state('Minnesota', ['Duluth', 'Minneapolis', 'Rochester', 'Saint Paul']),
      state('Mississippi', ['Biloxi', 'Gulfport', 'Jackson']),
      state('Missouri', ['Columbia', 'Kansas City', 'Springfield', 'St. Louis']),
      state('Montana', ['Billings', 'Bozeman', 'Helena', 'Missoula']),
      state('Nebraska', ['Bellevue', 'Lincoln', 'Omaha']),
      state('Nevada', ['Carson City', 'Henderson', 'Las Vegas', 'Reno']),
      state('New Hampshire', ['Concord', 'Manchester', 'Nashua']),
      state('New Jersey', ['Jersey City', 'Newark', 'Princeton', 'Trenton']),
      state('New Mexico', ['Albuquerque', 'Las Cruces', 'Santa Fe']),
      state('New York', ['Albany', 'Buffalo', 'New York City', 'Rochester', 'Syracuse']),
      state('North Carolina', ['Asheville', 'Charlotte', 'Durham', 'Raleigh']),
      state('North Dakota', ['Bismarck', 'Fargo', 'Grand Forks']),
      state('Ohio', ['Cincinnati', 'Cleveland', 'Columbus', 'Toledo']),
      state('Oklahoma', ['Norman', 'Oklahoma City', 'Tulsa']),
      state('Oregon', ['Bend', 'Eugene', 'Portland', 'Salem']),
      state('Pennsylvania', ['Allentown', 'Harrisburg', 'Philadelphia', 'Pittsburgh']),
      state('Rhode Island', ['Newport', 'Providence', 'Warwick']),
      state('South Carolina', ['Charleston', 'Columbia', 'Greenville']),
      state('South Dakota', ['Pierre', 'Rapid City', 'Sioux Falls']),
      state('Tennessee', ['Chattanooga', 'Knoxville', 'Memphis', 'Nashville']),
      state('Texas', ['Austin', 'Dallas', 'Fort Worth', 'Houston', 'San Antonio']),
      state('Utah', ['Ogden', 'Park City', 'Provo', 'Salt Lake City']),
      state('Vermont', ['Burlington', 'Montpelier', 'Rutland']),
      state('Virginia', ['Arlington', 'Norfolk', 'Richmond', 'Virginia Beach']),
      state('Washington', ['Bellevue', 'Olympia', 'Seattle', 'Spokane', 'Tacoma']),
      state('West Virginia', ['Charleston', 'Huntington', 'Morgantown']),
      state('Wisconsin', ['Green Bay', 'Madison', 'Milwaukee']),
      state('Wyoming', ['Casper', 'Cheyenne', 'Jackson']),
    ],
  },
];

export const OTHER_LOCATION_VALUE = '__other__';

export function findVenueCountry(countryName: string): VenueCountry | undefined {
  const needle = countryName.trim().toLowerCase();
  return VENUE_COUNTRIES.find((c) => c.name.toLowerCase() === needle);
}

export function findVenueState(countryName: string, stateName: string): VenueState | undefined {
  const country = findVenueCountry(countryName);
  if (!country) return undefined;
  const needle = stateName.trim().toLowerCase();
  return country.states.find((s) => s.name.toLowerCase() === needle);
}

export function statesForCountry(countryName: string): VenueState[] {
  return findVenueCountry(countryName)?.states ?? [];
}

export function citiesForState(countryName: string, stateName: string): string[] {
  return findVenueState(countryName, stateName)?.cities ?? [];
}

export function isKnownCountry(countryName: string): boolean {
  return Boolean(findVenueCountry(countryName));
}

export function isKnownState(countryName: string, stateName: string): boolean {
  return Boolean(findVenueState(countryName, stateName));
}

export function isKnownCity(countryName: string, stateName: string, cityName: string): boolean {
  const cities = citiesForState(countryName, stateName);
  const needle = cityName.trim().toLowerCase();
  return cities.some((c) => c.toLowerCase() === needle);
}
